/**
 * features/finance/Arrears.tsx
 * ------------------------------------------------------------------
 * P7 — Arrears + reminders. Overdue invoices rolled up per household:
 * one row (and one reminder) per family, combining every child's
 * past-due invoices — never one reminder per invoice. Projected late
 * interest (LKC charges 2%/month) is shown and can be folded into the
 * reminder amount. Reminders go out as an editable message to copy
 * (email/WhatsApp) or a formal A4 letter PDF; sending is logged.
 *
 * Interest here is projected, not posted — balances are unchanged. (A
 * separate "apply interest" run could post it as a charge later.)
 * ------------------------------------------------------------------ */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BellRing, FileDown, Copy, Check, ArrowLeft, Users, Clock, FileSpreadsheet, Filter, Lock, Search, X, Mail, MailCheck } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { AccentButton, GradientHero, SoftCard, StatChip, glassFieldClass } from '@/features/finance/ui';

import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { useData } from '@/contexts/DataContext';
import { useFinanceData } from '@/features/finance/useFinanceData';
import { recordFinanceAudit } from '@/lib/financeAudit';
import { SCHOOL } from '@/lib/school.config';
import { loadLogoDataUrl } from '@/lib/loadLogo';
import {
  planArrears, overdueRows, fmtPula, fmtBalance,
  effectiveAcademicHold, studentHasArrears, resolveHoldMode,
  FINANCE_COLLECTIONS, GLOBAL_HOLD_ID, type AcademicHoldMode, type Invoice,
  type OverdueRow,
} from '@/lib/financeCore';
import { collection, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { ARRANGEMENT_COLLECTION } from '@/lib/feeArrangements';
import { finance } from '@/features/finance/financeWrite';
import ReminderWording from '@/features/finance/ReminderWording';
import RemindersPanel from '@/features/finance/RemindersPanel';
import {
  ALUMNI_BUCKET, isAlumni, matchesFilter, markHeldBack, defaultSelection,
  describeSend, recipientsToSend, HOLD_BACK_LABEL, type RecipientRow,
} from '@/lib/arrearsRecipients';

interface OverdueLine { name: string; invoiceNo: string; balance: number; days: number; }


/**
 * What the server actually said.
 *
 * Both send paths used to report every failure as "check the reminder
 * function is deployed" — a guess, and for the commonest failure a wrong
 * one. The function was deployed and running; it was refusing the caller,
 * and that message sent people looking at the deployment for a permission
 * problem. A refusal has to say it is a refusal.
 */
function sendFailure(e: unknown): string {
  const raw = e instanceof Error ? e.message : String(e);
  const message = raw.replace(/^FirebaseError:\s*/i, '').trim();

  if (/permission-denied|finance role is required/i.test(message)) {
    return 'You are signed in without a role that may send fee reminders. '
      + 'A bursar, finance administrator or the head can send them.';
  }
  if (/not configured/i.test(message)) {
    return 'Email is not configured on the server, so nothing was sent.';
  }
  if (/not-found|internal|does not exist/i.test(message)) {
    return 'The reminder function did not answer — check it is deployed.';
  }
  return `Could not send — ${message || 'the server gave no reason.'}`;
}


/** What the server reports back about a bulk send. */
interface SendResult {
  sent: number;
  skipped: number;
  /** Named, so somebody can act on them today. */
  noAddress?: string[];
  failures?: { studentName: string; email: string; reason: string }[];
  /** Not attempted, because a bounce already proved the address dead. */
  undeliverable?: { studentName: string; email: string; reason: string }[];
}

/**
 * What actually happened, said plainly.
 *
 * This used to read "Sent 13 reminder(s) to 16 pupil(s)" — a sentence
 * shaped like success, where the only sign that three families were never
 * reached was a discrepancy between two numbers nobody compares. A send
 * that partly failed has to LOOK like a send that partly failed, and it
 * has to name who, because "3 failed" sends somebody hunting and three
 * names sends them to a keyboard.
 */
function sendOutcome(r: SendResult | undefined, attempted: number): string {
  const sent = r?.sent ?? 0;
  const failures = r?.failures ?? [];
  const noAddress = r?.noAddress ?? [];
  const dead = r?.undeliverable ?? [];

  const parts = [`Sent ${sent} reminder(s) to ${attempted} pupil(s).`];

  if (failures.length) {
    parts.push(
      `${failures.length} did NOT go through: `
      + failures.map((f) => `${f.studentName} (${f.email}) — ${f.reason}`).join('; '),
    );
  }
  if (noAddress.length) {
    parts.push(
      `${noAddress.length} had no parent email on file: ${noAddress.join(', ')}.`,
    );
  }
  /* NOT ATTEMPTED IS NOT FAILED. A bounce already proved these addresses
     dead, so sending again would only produce another bounce and another
     "sent" that never arrived. Named, because the fix is usually a typo
     somebody in the office can correct in a minute. */
  if (dead.length) {
    parts.push(
      `${dead.length} were NOT sent — a previous message to the address bounced: `
      + dead.map((f) => `${f.studentName} (${f.email})`).join('; ')
      + '. Correct the address and they will be chased on the next run.',
    );
  }
  if (!failures.length && !noAddress.length && !dead.length) {
    parts.push('Every one was accepted by the mail server.');
  }
  return parts.join(' ');
}

/** The tabs, and what each one is FOR. "Delivery" is the one that did not
 *  exist: the portal could say a reminder was sent but nothing could say
 *  whether it arrived, so 905 bounces accumulated in the bursar's inbox
 *  while every screen here reported success. */
const SUB_TABS = [
  { id: 'arrears', label: 'Overdue & chasing', icon: BellRing },
  { id: 'delivery', label: 'Delivery', icon: MailCheck },
  { id: 'access', label: 'Portal access', icon: Lock },
] as const;

type SubTabId = (typeof SUB_TABS)[number]['id'];

export default function Arrears() {
  const acad = useData();
  const data = useFinanceData();
  const { userData, permissions } = useAuth();
  const actor = { id: userData?.uid ?? 'unknown', email: userData?.email };
  const [sendingAll, setSendingAll] = useState(false);
  const [sendMsg, setSendMsg] = useState('');

  const defaultRate = data.settings.lateInterestMonthlyPct ?? 2;
  const [rate, setRate] = useState(defaultRate);
  const [includeInterest, setIncludeInterest] = useState(false);
  const [selFamilyId, setSelFamilyId] = useState('');
  const [selStudentId, setSelStudentId] = useState('');
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [reminded, setReminded] = useState<Set<string>>(new Set());
  const [holdModes, setHoldModes] = useState<Record<string, AcademicHoldMode>>({});
  const [holdBusy, setHoldBusy] = useState(false);
  const [subTab, setSubTab] = useState<SubTabId>('arrears');
  const [lockSearch, setLockSearch] = useState('');
  const [lockStudentId, setLockStudentId] = useState('');
  const [formFilter, setFormFilter] = useState<string>('all');
  const [classFilter, setClassFilter] = useState<string>('all');

  /* Families with a plan already agreed. A reminder to somebody the school
     has already had the conversation with reads as going back on it, so they
     start unticked — visibly, with the reason, not silently dropped. */
  const [arrangedFamilyIds, setArrangedFamilyIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    const unsub = onSnapshot(collection(db, ARRANGEMENT_COLLECTION), (snap) => {
      setArrangedFamilyIds(new Set(
        snap.docs
          .map((d) => d.data() as { familyId?: string; status?: string })
          .filter((a) => a.status === 'active' && a.familyId)
          .map((a) => a.familyId as string),
      ));
    }, () => {});
    return () => unsub();
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, FINANCE_COLLECTIONS.academicHolds), (snap) => {
      const m: Record<string, AcademicHoldMode> = {};
      snap.docs.forEach((d) => { m[d.id] = ((d.data() as { mode?: AcademicHoldMode }).mode ?? 'auto'); });
      setHoldModes(m);
    }, () => {});
    return () => unsub();
  }, []);

  /**
   * Set the academic-access override, KEYED ON THE PUPIL.
   *
   * The collection used to hold one doc per family. LKC administers nothing
   * at family level and most invoices carry no familyId, so the gate saw
   * almost none of the debt — 227 invoices owing P5.58m resolved to two
   * families in arrears. It now reads financeAcademicHolds/{studentId}, so a
   * row covering several pupils writes several docs: releasing "the family"
   * means releasing each child in it, explicitly.
   *
   * The school-wide sentinel is passed through unchanged — it is not a pupil
   * id and has always been its own key.
   */
  async function setHold(scope: string | string[], mode: AcademicHoldMode) {
    if (!permissions.canManageFinanceConfig || holdBusy) return;
    const ids = (Array.isArray(scope) ? scope : [scope]).filter(Boolean);
    if (!ids.length) return;
    setHoldBusy(true);
    try {
      for (const id of ids) {
        if (mode === 'auto') await finance.academicHolds.delete(id);
        else await finance.academicHolds.set(id, { studentId: id, mode, updatedBy: actor.email ?? actor.id });
        await recordFinanceAudit({ action: 'arrears.academicHold', actor, subjectId: id, after: { mode } });
      }
      setHoldModes((prev) => {
        const next = { ...prev };
        ids.forEach((id) => { next[id] = mode; });
        return next;
      });
    } finally { setHoldBusy(false); }
  }

  /** The pupils an arrears row covers — what a hold now applies to. */
  const pupilIdsForRow = (familyId: string): string[] => [
    ...new Set(
      data.invoices
        .filter((i) => (i.familyId || `__${i.studentId}`) === familyId && i.status !== 'void')
        .map((i) => i.studentId)
        .filter(Boolean),
    ),
  ];

  useEffect(() => { setRate(data.settings.lateInterestMonthlyPct ?? 2); }, [data.settings.lateInterestMonthlyPct]);

  const studentName = useMemo(() => {
    const m: Record<string, string> = {};
    acad.students.forEach((s) => { m[s.id] = s.fullName; });
    data.invoices.forEach((i) => { if (i.studentName && !m[i.studentId]) m[i.studentId] = i.studentName; });
    return m;
  }, [acad.students, data.invoices]);

  const asOf = useMemo(() => new Date(), []);
  const plan = useMemo(
    () => planArrears({ invoices: data.invoices, settings: data.settings, monthlyRatePct: rate, asOf }),
    [data.invoices, data.settings, rate, asOf],
  );

  /* Every line the reminder will quote — netted, so the letter never asks
     for money the pupil has already handed over on another invoice. */
  const rows = useMemo(() => overdueRows(data.invoices, asOf), [data.invoices, asOf]);

  const lineOf = (r: OverdueRow): OverdueLine => ({
    name: studentName[r.invoice.studentId] ?? r.invoice.studentName ?? '—',
    invoiceNo: r.invoice.invoiceNo,
    balance: r.overdue,
    days: r.oldest,
  });

  const overdueFor = (familyId: string): OverdueLine[] => rows
    .filter((r) => (r.invoice.familyId || `__${r.invoice.studentId}`) === familyId)
    .map(lineOf)
    .sort((a, b) => b.days - a.days);

  const selFamily = plan.families.find((f) => f.familyId === selFamilyId);
  const famMeta = selFamily ? acad.familyById(selFamily.familyId) : undefined;
  const famName = famMeta?.name ?? 'Family account';
  const contact = famMeta?.primaryContact?.name ?? '';
  const lines = selFamily ? overdueFor(selFamily.familyId) : [];

  // Seed the editable message when a family is selected or interest toggles.
  useEffect(() => {
    if (!selFamily) return;
    setCopied(false);
    setMessage(reminderText({
      contact, family: famName, date: asOf.toISOString().slice(0, 10),
      overdue: selFamily.overdue, interest: selFamily.interest, includeInterest,
      lines, schoolName: SCHOOL.name,
    }));
  }, [selFamilyId, includeInterest]); // eslint-disable-line react-hooks/exhaustive-deps

  const amountDue = selFamily ? (includeInterest ? selFamily.overdue + selFamily.interest : selFamily.overdue) : 0;

  const copyMessage = async () => {
    try { await navigator.clipboard.writeText(message); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard blocked */ }
  };

  const downloadLetter = async () => {
    if (!selFamily) return;
    const logo = await loadLogoDataUrl();
    buildReminderPdf({
      contact, family: famName, contactEmail: famMeta?.primaryContact?.email,
      date: asOf.toISOString().slice(0, 10), overdue: selFamily.overdue,
      interest: selFamily.interest, includeInterest, lines,
    }, logo).save(`reminder_${famName.replace(/\s+/g, '_')}.pdf`);
  };

  const logReminder = async () => {
    if (!selFamily) return;
    await recordFinanceAudit({ action: 'arrears.reminder', actor, subjectId: selFamily.familyId, after: { amount: amountDue, includeInterest } });
    setReminded((prev) => new Set(prev).add(selFamily.familyId));
  };

  // ---- Filter by form / class (family-scoped: keep a family if ANY child matches) ----
  const studentById = useMemo(() => {
    const m: Record<string, (typeof acad.students)[number]> = {};
    acad.students.forEach((s) => { m[s.id] = s; });
    return m;
  }, [acad.students]);

  const formOptions = useMemo(
    () => Array.from(new Set(acad.classes.map((c) => c.form))).sort(),
    [acad.classes],
  );
  const classOptions = useMemo(
    () => acad.classes
      .filter((c) => formFilter === 'all' || c.form === formFilter)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [acad.classes, formFilter],
  );

  /* Classes the school has archived. A pupil parked in one has left, the
     same as a pupil marked 'left' — the school has used both, so both count. */
  const archivedClassIds = useMemo(
    () => new Set(acad.classes.filter((c) => c.archived).map((c) => c.id)),
    [acad.classes],
  );
  const formOf = useCallback(
    (classId: string) => acad.classById(classId)?.form,
    [acad],
  );

  const filteredFamilies = useMemo(() => {
    if (formFilter === 'all' && classFilter === 'all') return plan.families;
    return plan.families.filter((f) => f.studentIds.some((sid) => {
      const s = studentById[sid];
      if (!s) return false;
      return matchesFilter(s, archivedClassIds, formFilter, classFilter, formOf);
    }));
  }, [plan.families, formFilter, classFilter, studentById, archivedClassIds, formOf]);

  const filterLabel = classFilter === ALUMNI_BUCKET
    ? 'Alumni'
    : classFilter !== 'all'
      ? (acad.classById(classFilter)?.name ?? 'Class')
      : formFilter !== 'all' ? `Form ${formFilter}` : 'All families';

  const filterActive = formFilter !== 'all' || classFilter !== 'all';

  // Per-student overdue totals. A pupil whose credits cover their past-due
  // invoices does not appear here at all — they owe the school nothing.
  const studentArrears = useMemo(() => {
    const m = new Map<string, { overdue: number; oldest: number; invoices: number }>();
    for (const r of rows) {
      const cur = m.get(r.invoice.studentId) ?? { overdue: 0, oldest: 0, invoices: 0 };
      cur.overdue += r.overdue;
      cur.oldest = Math.max(cur.oldest, r.oldest);
      cur.invoices += 1;
      m.set(r.invoice.studentId, cur);
    }
    return m;
  }, [rows]);

  // Individual students in the selected form/class who are in arrears.
  const studentRows = useMemo(() => {
    if (!filterActive) return [] as { studentId: string; familyKey: string; name: string; className: string; family: string; admissionNo: string; overdue: number; oldest: number }[];
    const rows: { studentId: string; familyKey: string; name: string; className: string; family: string; admissionNo: string; overdue: number; oldest: number }[] = [];
    for (const [sid, agg] of studentArrears) {
      const s = studentById[sid];
      if (!s) continue;
      if (!matchesFilter(s, archivedClassIds, formFilter, classFilter, formOf)) continue;
      const cls = acad.classById(s.currentClass);
      const fam = s.familyId ? acad.familyById(s.familyId) : undefined;
      const gone = isAlumni(s, archivedClassIds);
      rows.push({
        studentId: sid,
        familyKey: s.familyId || `__${sid}`,
        name: s.fullName,
        // Alumni keep their old class on the record — it is worth having —
        // but it reads as 'former', so nobody mistakes them for a current
        // pupil while looking at a list of people to chase.
        className: gone
          ? `Alumni — was ${cls?.name ?? s.currentClass ?? 'unknown'}`
          : (cls?.name ?? s.currentClass),
        family: fam?.name ?? '—',
        admissionNo: s.admissionNumber ?? '',
        overdue: agg.overdue,
        oldest: agg.oldest,
      });
    }
    return rows.sort((a, b) => b.oldest - a.oldest || a.name.localeCompare(b.name));
  }, [filterActive, studentArrears, formFilter, classFilter, studentById, acad, archivedClassIds, formOf]);

  const studentOverdueTotal = useMemo(() => studentRows.reduce((a, r) => a + r.overdue, 0), [studentRows]);

  /* ---- who this send actually goes to ----------------------------------
   *
   * The school asked to be able to leave people out of a class send —
   * specifically anyone who has agreed a payment plan. Those start unticked
   * with the reason showing, rather than being dropped: a bursar who cannot
   * see who was skipped cannot put one back in, and cannot tell whether the
   * list was right. */
  const recipientRows = useMemo<RecipientRow[]>(
    () => markHeldBack(
      studentRows.map((r) => ({
        studentId: r.studentId,
        name: r.name,
        className: r.className,
        familyId: r.familyKey.startsWith('__') ? undefined : r.familyKey,
        overdue: r.overdue,
      })),
      { holdModes, arrangedFamilyIds },
    ),
    [studentRows, holdModes, arrangedFamilyIds],
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  /* Re-seeded whenever the list changes. A selection left over from the
     previous class is exactly how the wrong person gets chased for money. */
  useEffect(() => { setSelectedIds(defaultSelection(recipientRows)); }, [recipientRows]);

  /* Everyone still at the school who is in arrears.
   *
   * "Email all overdue" used to mean literally every overdue invoice, which
   * included pupils who had left — they keep their old class on the record,
   * so nothing distinguished them. That is the send the school found out
   * about the hard way. It now passes an explicit list of CURRENT pupils;
   * former pupils are reached through the Alumni filter, deliberately. */
  const currentPupilsInArrears = useMemo(
    () => [...studentArrears.keys()].filter((sid) => {
      const st = studentById[sid];
      return !!st && !isAlumni(st, archivedClassIds);
    }),
    [studentArrears, studentById, archivedClassIds],
  );

  const alumniInArrearsCount = useMemo(
    () => [...studentArrears.keys()].filter((sid) => {
      const st = studentById[sid];
      return !!st && isAlumni(st, archivedClassIds);
    }).length,
    [studentArrears, studentById, archivedClassIds],
  );

  const emailAllOverdue = async () => {
    if (sendingAll || !currentPupilsInArrears.length) return;
    setSendingAll(true); setSendMsg('');
    try {
      const fn = httpsCallable<{ studentIds: string[] }, SendResult>(
        functions, 'sendFeeRemindersNow',
      );
      const r = await fn({ studentIds: currentPupilsInArrears });
      setSendMsg(
        sendOutcome(r.data, currentPupilsInArrears.length)
        + (alumniInArrearsCount
          ? ` ${alumniInArrearsCount} former pupil(s) in arrears were not included — chase them from the Alumni filter.`
          : ''),
      );
    } catch (e) {
      console.error(e);
      setSendMsg(sendFailure(e));
    } finally { setSendingAll(false); }
  };

  const heldBackBy = useMemo(() => {
    const m: Record<string, NonNullable<RecipientRow['heldBack']>> = {};
    for (const r of recipientRows) if (r.heldBack) m[r.studentId] = r.heldBack;
    return m;
  }, [recipientRows]);

  /* The first real debtor on the list. A message that reads well against a
     made-up name and badly against an actual one is worth finding out about
     here rather than from a parent. */
  const previewFacts = useMemo(() => {
    const r = studentRows[0];
    if (!r) return undefined;
    return {
      child: r.name,
      amount: fmtPula(r.overdue),
      daysOverdue: r.oldest,
      instalment: 'the outstanding instalment',
    };
  }, [studentRows]);

  const [sendingClass, setSendingClass] = useState(false);
  const [classSendMsg, setClassSendMsg] = useState('');

  async function emailSelected() {
    const ids = recipientsToSend(recipientRows, selectedIds);
    if (!ids.length || sendingClass) return;
    setSendingClass(true);
    setClassSendMsg('');
    try {
      const fn = httpsCallable<{ studentIds: string[] }, SendResult>(
        functions, 'sendFeeRemindersNow',
      );
      const r = await fn({ studentIds: ids });
      setClassSendMsg(`${sendOutcome(r.data, ids.length)} (${filterLabel})`);
      await recordFinanceAudit({
        action: 'arrears.bulkReminder',
        actor,
        subjectId: filterLabel,
        after: { scope: filterLabel, sent: ids.length, heldBack: recipientRows.length - ids.length },
      });
    } catch (e) {
      console.error(e);
      setClassSendMsg(sendFailure(e));
    } finally { setSendingClass(false); }
  }

  // ---- Single-student view (from the students-in-arrears list) ----
  const selStudent = useMemo(() => {
    if (!selStudentId) return null;
    const s = studentById[selStudentId];
    if (!s) return null;
    const cls = acad.classById(s.currentClass);
    const fam = s.familyId ? acad.familyById(s.familyId) : undefined;
    return {
      id: s.id,
      name: s.fullName,
      className: cls?.name ?? s.currentClass,
      admissionNo: s.admissionNumber ?? '',
      familyKey: s.familyId || `__${s.id}`,
      familyName: fam?.name ?? '—',
    };
  }, [selStudentId, studentById, acad]);

  const studentLines = useMemo<OverdueLine[]>(() => {
    if (!selStudent) return [];
    return rows
      .filter((r) => r.invoice.studentId === selStudent.id)
      .map((r) => ({ ...lineOf(r), name: selStudent.name }))
      .sort((a, b) => b.days - a.days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selStudent, rows]);
  const studentLineTotal = useMemo(() => studentLines.reduce((a, l) => a + l.balance, 0), [studentLines]);

  const exportRows = useMemo(() => filteredFamilies.map((f) => {
    const meta = acad.familyById(f.familyId);
    return {
      family: meta?.name ?? 'Unlinked',
      contact: meta?.primaryContact?.name ?? '',
      children: f.studentIds.length,
      oldest: f.oldestDays,
      overdue: f.overdue,
      interest: f.interest,
      total: f.overdue + f.interest,
    };
  }), [filteredFamilies, acad]);

  const filteredTotals = useMemo(() => ({
    overdue: exportRows.reduce((a, r) => a + r.overdue, 0),
    interest: exportRows.reduce((a, r) => a + r.interest, 0),
    total: exportRows.reduce((a, r) => a + r.total, 0),
  }), [exportRows]);

  const exportSlug = `arrears_${filterLabel.replace(/[^\w]+/g, '_')}`;

  function downloadCsv() {
    const esc = (v: string | number) => `"${String(v).replace(/"/g, '""')}"`;
    let head: string[]; let body: string[];
    if (filterActive) {
      head = ['Student', 'Admission no.', 'Class', 'Family', 'Overdue', 'Oldest (days)'];
      body = studentRows.map((r) => [r.name, r.admissionNo, r.className, r.family, r.overdue, r.oldest].map(esc).join(','));
    } else {
      head = ['Family', 'Primary contact', 'Children', 'Oldest (days)', 'Overdue', 'Interest', 'Total'];
      body = exportRows.map((r) => [r.family, r.contact, r.children, r.oldest, r.overdue, r.interest, r.total].map(esc).join(','));
    }
    const csv = [head.map(esc).join(','), ...body].join('\r\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${exportSlug}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  async function downloadArrearsPdf() {
    const logo = await loadLogoDataUrl().catch(() => null);
    const pdf = filterActive
      ? buildStudentArrearsPdf(studentRows, { filterLabel, date: asOf.toISOString().slice(0, 10), total: studentOverdueTotal }, logo)
      : buildArrearsListPdf(exportRows, { filterLabel, date: asOf.toISOString().slice(0, 10), rate, totals: filteredTotals }, logo);
    pdf.save(`${exportSlug}.pdf`);
  }

  if (acad.loading || data.loading) {
    return (
      <div className="space-y-6">
        <GradientHero icon={BellRing} title="Arrears" subtitle="Overdue balances and family reminders" />
        <div className="flex justify-center py-16"><Spinner label="Loading…" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <GradientHero icon={BellRing} title="Arrears" subtitle="Overdue balances and family reminders" />

      {/* THREE TASKS, NOT THREE VIEWS OF ONE. Chasing a family, checking
          whether the chase reached anybody, and cutting off the portal are
          done by different people on different days, and the middle one had
          nowhere to live at all — which is how the school sent 905 reminders
          and reached 57 families without anything on screen saying so.

          The list, the selection and the send stay together on one tab: you
          pick the rows you are sending to out of the table, so separating
          them would break the only workflow that matters here. */}
      <div className="flex gap-1 p-1 rounded-xl bg-gray-100 dark:bg-gray-800">
        {SUB_TABS
          .filter((t) => (t.id === 'access' ? permissions.canManageFinanceConfig : true))
          .map(({ id, label, icon: Icon }) => (
            <button key={id} onClick={() => setSubTab(id)}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                subTab === id ? 'seg-on' : 'seg-off'
              }`}>
              <Icon size={15} /> {label}
            </button>
          ))}
      </div>

      {subTab === 'access' && renderPortalAccess()}

      {subTab === 'delivery' && <RemindersPanel />}

      {subTab === 'arrears' && (<>
      {/* Controls */}
      <SoftCard hover={false}>
        <div className="flex flex-wrap items-end gap-4">
          <label className="block">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1 block">Late interest (per month)</span>
            <div className="flex items-center gap-1">
              <input type="number" value={rate} onChange={(e) => setRate(Number(e.target.value) || 0)} className={glassFieldClass('py-2 w-20 text-right')} />
              <span className="text-sm text-gray-400">%</span>
            </div>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 pb-2">
            <input type="checkbox" checked={includeInterest} onChange={(e) => setIncludeInterest(e.target.checked)} className="rounded" />
            Include interest in reminders
          </label>
          <div className="flex-1" />
          <div className="text-right">
            <div className="text-xs text-gray-400">Projected interest</div>
            <div className="text-lg font-bold text-gray-800 dark:text-gray-100">{fmtPula(plan.totalInterest)}</div>
          </div>
        </div>
      </SoftCard>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <StatChip tone="red" value={fmtPula(plan.totalOverdue)} label="Total overdue" icon={Clock} />
        <StatChip tone="slate" value={String(plan.familyCount)} label="Families in arrears" icon={Users} />
        <StatChip tone="gold" value={fmtPula(plan.totalOverdue + plan.totalInterest)} label="Overdue + interest" icon={BellRing} />
      </div>

      {/* School-wide parent-portal arrears blocking (per-family overrides still win) */}
      {permissions.canManageFinanceConfig && (() => {
        const gMode = holdModes[GLOBAL_HOLD_ID] ?? 'auto';
        const GBtn = ({ m, label, desc }: { m: AcademicHoldMode; label: string; desc: string }) => (
          <button
            onClick={() => setHold(GLOBAL_HOLD_ID, m)}
            disabled={holdBusy || gMode === m}
            className={`flex-1 text-left px-3 py-2 rounded-lg border transition-colors ${
              gMode === m
                ? 'bg-primary text-white border-primary'
                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
            } disabled:opacity-60`}
          >
            <div className="text-sm font-semibold">{label}</div>
            <div className={`text-xs mt-0.5 ${gMode === m ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>{desc}</div>
          </button>
        );
        return (
          <SoftCard hover={false}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Parent portal — arrears blocking (school-wide)</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Whether overdue fees pause a parent's academic access. Per-family overrides (set on each family) always win.
                </p>
              </div>
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
                gMode === 'release' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                : gMode === 'hold' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
              }`}>
                {gMode === 'release' ? 'Relaxed — all allowed' : gMode === 'hold' ? 'Enforced — all held' : 'Automatic'}
              </span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 mt-3">
              <GBtn m="auto" label="Automatic" desc="Block only families in arrears — the normal policy." />
              <GBtn m="release" label="Relax (allow all)" desc="No arrears block; parents keep full access. Use this after seeding balances." />
              <GBtn m="hold" label="Enforce (hold all)" desc="Pause every family's academic access until cleared." />
            </div>
          </SoftCard>
        );
      })()}

      {!selFamily && plan.familyCount > 0 && (
        <div className="flex items-center gap-3">
          <button
            onClick={emailAllOverdue}
            disabled={sendingAll}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white bg-[#1b3a6b] hover:bg-[#2d5295] disabled:opacity-50"
          >
            <BellRing size={15} />
            {sendingAll ? 'Sending…' : `Email all ${currentPupilsInArrears.length} current pupils in arrears`}
          </button>
          {sendMsg && <span className="text-sm text-gray-500 dark:text-gray-400">{sendMsg}</span>}
        </div>
      )}

      {selStudent ? (
        /* ---- Single student (arrears) ---- */
        <>
          <button onClick={() => setSelStudentId('')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200">
            <ArrowLeft size={15} /> Back to arrears
          </button>

          <SoftCard hover={false}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">{selStudent.name}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {selStudent.className}{selStudent.admissionNo ? ` · ${selStudent.admissionNo}` : ''} · {selStudent.familyName}
                </p>
              </div>
              <AccentButton icon={Users} onClick={() => { setSelFamilyId(selStudent.familyKey); setSelStudentId(''); }}>
                View family statement
              </AccentButton>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <StatChip tone="red" value={fmtPula(studentLineTotal)} label="Overdue (this student)" />
              <StatChip tone="gold" value={String(studentLines.length)} label="Overdue invoice(s)" />
            </div>
          </SoftCard>

          <SoftCard pad="p-0" hover={false}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700"><h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Overdue invoices</h3></div>
            <div className="p-5 overflow-x-auto">
              {studentLines.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No overdue invoices for this student.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 pr-3 font-medium">Invoice</th>
                    <th className="py-2 px-3 font-medium text-right">Balance</th>
                    <th className="py-2 pl-3 font-medium text-right">Days overdue</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                    {studentLines.map((l) => (
                      <tr key={l.invoiceNo}>
                        <td className="py-2 pr-3 text-gray-500 dark:text-gray-400">{l.invoiceNo}</td>
                        <td className="py-2 px-3 text-right font-medium text-gray-800 dark:text-gray-100">{fmtBalance(l.balance)}</td>
                        <td className="py-2 pl-3 text-right text-red-600 dark:text-red-400">{l.days}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </SoftCard>
        </>
      ) : selFamily ? (
        /* ---- Reminder draft ---- */
        <>
          <button onClick={() => setSelFamilyId('')} className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200">
            <ArrowLeft size={15} /> Back to arrears
          </button>

          <SoftCard hover={false}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">{famName}</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">{contact || 'Primary contact not set'} · {selFamily.invoiceCount} overdue invoice(s) · oldest {selFamily.oldestDays} days</p>
              </div>
              {reminded.has(selFamily.familyId) && <span className="inline-flex items-center gap-1 text-sm text-emerald-600 dark:text-emerald-400"><Check size={15} /> Reminder logged</span>}
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              <StatChip tone="red" value={fmtPula(selFamily.overdue)} label="Overdue" />
              <StatChip tone="gold" value={fmtPula(selFamily.interest)} label={`Interest @ ${rate}%/mo`} />
              <StatChip tone="blue" value={fmtPula(amountDue)} label={includeInterest ? 'Amount due (incl. interest)' : 'Amount due'} />
            </div>
          </SoftCard>

          {/* Parent academic access (Phase 3 fee-gate override) */}
          {(() => {
            const rowPupils = pupilIdsForRow(selFamily.familyId);
            // Every pupil on the row shares a mode only when they agree; a
            // mixed row reads as 'auto' so setting one applies to all.
            const modes = rowPupils.map((id) => holdModes[id] ?? 'auto');
            const mode: AcademicHoldMode =
              modes.length && modes.every((m) => m === modes[0]) ? modes[0] : 'auto';
            const held = effectiveAcademicHold(true, mode); // in arrears by definition here
            const Btn = ({ m, label }: { m: AcademicHoldMode; label: string }) => (
              <button
                onClick={() => setHold(rowPupils, m)}
                disabled={holdBusy || mode === m}
                className={`px-3 py-1.5 rounded-lg text-sm border ${
                  mode === m
                    ? 'bg-primary text-white border-primary'
                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                } disabled:opacity-60`}
              >
                {label}
              </button>
            );
            return (
              <SoftCard hover={false}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Parent academic access</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {held
                        ? 'Paused — parents see only Fees until the balance is cleared.'
                        : 'Allowed — parents can see reports, marks and attendance despite arrears.'}
                      {mode !== 'auto' && (
                        <span className="ml-1 italic">
                          ({mode === 'release' ? 'manually allowed' : 'manually held'})
                        </span>
                      )}
                    </p>
                  </div>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full ${held ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'}`}>
                    {held ? 'Held' : 'Allowed'}
                  </span>
                </div>
                {permissions.canManageFinanceConfig ? (
                  <div className="flex items-center gap-2 mt-3 flex-wrap">
                    <Btn m="auto" label="Automatic" />
                    <Btn m="release" label="Allow access" />
                    <Btn m="hold" label="Force hold" />
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 mt-2">A finance admin can change this.</p>
                )}
              </SoftCard>
            );
          })()}

          <SoftCard pad="p-0" hover={false}>
            <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700"><h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Overdue invoices</h3></div>
            <div className="p-5 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 pr-3 font-medium">Student</th>
                  <th className="py-2 px-3 font-medium">Invoice</th>
                  <th className="py-2 px-3 font-medium text-right">Balance</th>
                  <th className="py-2 pl-3 font-medium text-right">Days overdue</th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {lines.map((l) => (
                    <tr key={l.invoiceNo}>
                      <td className="py-2 pr-3 text-gray-800 dark:text-gray-100">{l.name}</td>
                      <td className="py-2 px-3 text-gray-500 dark:text-gray-400">{l.invoiceNo}</td>
                      <td className="py-2 px-3 text-right font-medium text-gray-800 dark:text-gray-100">{fmtBalance(l.balance)}</td>
                      <td className="py-2 pl-3 text-right text-red-600 dark:text-red-400">{l.days}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </SoftCard>

          <SoftCard hover={false}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Reminder message</h3>
              <span className="text-xs text-gray-400">Edit freely, then copy or download</span>
            </div>
            <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={10}
              className={glassFieldClass('w-full text-sm font-mono leading-relaxed')} />
            <div className="flex items-center justify-end gap-2 mt-3 flex-wrap">
              <button onClick={copyMessage} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                {copied ? <><Check size={15} className="text-emerald-500" /> Copied</> : <><Copy size={15} /> Copy message</>}
              </button>
              <button onClick={downloadLetter} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700">
                <FileDown size={15} /> Download letter
              </button>
              <AccentButton icon={BellRing} onClick={logReminder}>Log reminder sent</AccentButton>
            </div>
          </SoftCard>
        </>
      ) : (
        /* ---- Arrears list ---- */
        <SoftCard pad="p-0" hover={false}>
          <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">{filterActive ? 'Students in arrears' : 'Families in arrears'}</h3>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Filter size={13} /> Filter</span>
              <select
                value={formFilter}
                onChange={(e) => { setFormFilter(e.target.value); setClassFilter('all'); }}
                className={glassFieldClass('py-1.5 text-sm w-32')}
              >
                <option value="all">All forms</option>
                {formOptions.map((f) => <option key={f} value={f}>Form {f}</option>)}
              </select>
              <select
                value={classFilter}
                onChange={(e) => setClassFilter(e.target.value)}
                className={glassFieldClass('py-1.5 text-sm w-40')}
              >
                <option value="all">All classes</option>
                {classOptions.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                {/* Former pupils, on their own. They still owe what they owe
                    and can still be chased — it just has to be a decision,
                    which is what picking this option is. */}
                <option value={ALUMNI_BUCKET}>Alumni (former pupils)</option>
              </select>
              <button
                onClick={downloadCsv}
                disabled={(filterActive ? studentRows.length : exportRows.length) === 0}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
              >
                <FileSpreadsheet size={15} /> CSV
              </button>
              <button
                onClick={downloadArrearsPdf}
                disabled={(filterActive ? studentRows.length : exportRows.length) === 0}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40"
              >
                <FileDown size={15} /> PDF
              </button>
            </div>
          </div>
          {filterActive && (
            <div className="px-5 pt-3 -mb-2 space-y-2">
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Showing {filterLabel} · {studentRows.length} student{studentRows.length === 1 ? '' : 's'} in arrears · {fmtPula(studentOverdueTotal)} overdue
              </div>
              {recipientRows.length > 0 && permissions.canManageFinanceConfig && (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                  <button
                    onClick={() => setSelectedIds(new Set(recipientRows.map((r) => r.studentId)))}
                    className="text-xs px-2 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Select all
                  </button>
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    className="text-xs px-2 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Select none
                  </button>
                  <button
                    onClick={() => setSelectedIds(defaultSelection(recipientRows))}
                    className="text-xs px-2 py-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    Reset
                  </button>
                  {/* SAYS WHAT IT IS ABOUT TO DO. "Email 34" and "Email 34 of
                      51, holding back 17 with arrangements" are different
                      sentences, and only one lets somebody catch a mistake. */}
                  <span className="text-xs text-gray-600 dark:text-gray-300 mx-1">
                    {describeSend(recipientRows, selectedIds)}
                  </span>
                  <button
                    onClick={emailSelected}
                    disabled={sendingClass || selectedIds.size === 0}
                    className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm bg-[var(--accent-link,#0d9488)] text-white disabled:opacity-40"
                  >
                    <Mail size={14} />
                    {sendingClass ? 'Sending…' : `Email ${selectedIds.size}`}
                  </button>
                </div>
              )}
              {classSendMsg && (
                <p className="text-xs text-emerald-700 dark:text-emerald-300">{classSendMsg}</p>
              )}
              {/* Next to the send, not buried in settings: the moment to
                  check the wording is the moment before it goes out. */}
              <ReminderWording
                settings={data.settings}
                canEdit={permissions.canManageFinanceConfig}
                sample={previewFacts}
              />
            </div>
          )}
          <div className="p-5 overflow-x-auto">
            {filterActive ? (
              /* ---- Per-student view (filtered by form/class) ---- */
              studentRows.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-4">No students in arrears in {filterLabel}.</p>
              ) : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-2 pr-2 font-medium w-8"></th>
                    <th className="py-2 pr-3 font-medium">Student</th>
                    <th className="py-2 px-3 font-medium">Class</th>
                    <th className="py-2 px-3 font-medium">Family</th>
                    <th className="py-2 px-3 font-medium text-right">Oldest</th>
                    <th className="py-2 px-3 font-medium text-right">Overdue</th>
                    <th className="py-2 pl-3"></th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                    {studentRows.slice(0, 300).map((r) => (
                      <tr key={r.studentId} className="hover:bg-[var(--accent-link,#0d9488)]/5 cursor-pointer" onClick={() => setSelStudentId(r.studentId)}>
                        <td className="py-2 pr-2" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(r.studentId)}
                            onChange={(e) => setSelectedIds((prev) => {
                              const next = new Set(prev);
                              if (e.target.checked) next.add(r.studentId); else next.delete(r.studentId);
                              return next;
                            })}
                            className="rounded border-gray-300 dark:border-gray-600"
                          />
                        </td>
                        <td className="py-2 pr-3 text-gray-800 dark:text-gray-100 font-medium">
                          {r.name}
                          {r.admissionNo && <span className="ml-1.5 text-xs font-normal text-gray-400">{r.admissionNo}</span>}
                          {heldBackBy[r.studentId] && (
                            <span className="ml-2 text-xs font-normal px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                              {HOLD_BACK_LABEL[heldBackBy[r.studentId]]}
                            </span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-gray-600 dark:text-gray-300">{r.className}</td>
                        <td className="py-2 px-3 text-gray-500 dark:text-gray-400">{r.family}</td>
                        <td className="py-2 px-3 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${r.oldest >= 90 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : r.oldest >= 60 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                            {r.oldest}d
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right font-semibold text-gray-800 dark:text-gray-100">{fmtPula(r.overdue)}</td>
                        <td className="py-2 pl-3 text-right"><span className="text-xs font-medium text-[var(--accent-link,#0d9488)]">View →</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : filteredFamilies.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 py-4">
                {plan.families.length === 0 ? "No overdue balances — everyone's current. 🎉" : 'No families in arrears match this filter.'}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                  <th className="py-2 pr-3 font-medium">Family</th>
                  <th className="py-2 px-3 font-medium text-right">Children</th>
                  <th className="py-2 px-3 font-medium text-right">Oldest</th>
                  <th className="py-2 px-3 font-medium text-right">Overdue</th>
                  <th className="py-2 px-3 font-medium text-right">Interest</th>
                  <th className="py-2 pl-3"></th>
                </tr></thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-700/50">
                  {filteredFamilies.slice(0, 150).map((f) => {
                    const meta = acad.familyById(f.familyId);
                    return (
                      <tr key={f.familyId} className="hover:bg-[var(--accent-link,#0d9488)]/5 cursor-pointer" onClick={() => setSelFamilyId(f.familyId)}>
                        <td className="py-2 pr-3 text-gray-800 dark:text-gray-100 font-medium">
                          {meta?.name ?? 'Unlinked'}
                          {reminded.has(f.familyId) && <Check size={13} className="inline ml-1.5 text-emerald-500" />}
                        </td>
                        <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-300">{f.studentIds.length}</td>
                        <td className="py-2 px-3 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${f.oldestDays >= 90 ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : f.oldestDays >= 60 ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`}>
                            {f.oldestDays}d
                          </span>
                        </td>
                        <td className="py-2 px-3 text-right font-semibold text-gray-800 dark:text-gray-100">{fmtPula(f.overdue)}</td>
                        <td className="py-2 px-3 text-right text-gray-500 dark:text-gray-400">{f.interest > 0 ? fmtPula(f.interest) : '—'}</td>
                        <td className="py-2 pl-3 text-right"><span className="text-xs font-medium text-[var(--accent-link,#0d9488)]">Reminder →</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {!filterActive && filteredFamilies.length > 150 && <p className="text-xs text-gray-400 mt-3">Showing 150 of {filteredFamilies.length}.</p>}
            {filterActive && studentRows.length > 300 && <p className="text-xs text-gray-400 mt-3">Showing 300 of {studentRows.length}.</p>}
          </div>
        </SoftCard>
      )}
      </>)}
    </div>
  );

  /* ================================================================
     PORTAL ACCESS — bursar lock/unlock per family + school-wide default.
     ================================================================ */
  function renderPortalAccess() {
    const globalMode = holdModes[GLOBAL_HOLD_ID] ?? 'auto';
    const q = lockSearch.trim().toLowerCase();
    const matches = q
      ? acad.students.filter((s) => (s.fullName || '').toLowerCase().includes(q)).slice(0, 20)
      : [];
    const sel = acad.students.find((s) => s.id === lockStudentId);
    /* THE HOLD SCOPE IS THE PUPIL. It used to be `familyId || __studentId`,
       and when the gate moved to pupil keys this button kept writing the old
       one — so "Unlock (release)" landed in a document nothing reads and the
       bursar watched a pupil stay locked after releasing them. The family id
       is still resolved below, but only to look up a display name. */
    const holdScope = sel ? sel.id : '';
    const fam = sel && sel.familyId ? acad.familyById(sel.familyId) : undefined;
    // THIS PUPIL's invoices, not the family's. Every invoice carries a
    // studentId; most carry no familyId at all.
    const famInvoices: Invoice[] = sel
      ? data.invoices.filter((i) => i.studentId === sel.id && i.status !== 'void')
      : [];
    const netBalance = famInvoices.reduce((s, i) => s + (i.balance || 0), 0);
    const arrears = sel ? studentHasArrears(famInvoices, sel.id) : false;
    const mode = sel ? (holdModes[sel.id] ?? 'auto') : 'auto';
    const held = effectiveAcademicHold(arrears, resolveHoldMode(mode, globalMode));

    const HBtn = ({ scope, current, m, label, desc }: { scope: string; current: AcademicHoldMode; m: AcademicHoldMode; label: string; desc: string }) => {
      const cur = current === m;
      return (
        <button onClick={() => setHold(scope, m)} disabled={holdBusy || cur}
          className={`flex-1 text-left px-3 py-2 rounded-lg border transition-colors ${
            cur ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
          } disabled:opacity-60`}>
          <div className="text-sm font-semibold">{label}</div>
          <div className={`text-xs mt-0.5 ${cur ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>{desc}</div>
        </button>
      );
    };

    return (
      <div className="space-y-4">
        <SoftCard hover={false}>
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">School-wide default</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
            Whether overdue fees pause a parent's academic access across the school. Per-family settings below always win.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <HBtn scope={GLOBAL_HOLD_ID} current={globalMode} m="auto" label="Automatic" desc="Hold only families in arrears" />
            <HBtn scope={GLOBAL_HOLD_ID} current={globalMode} m="release" label="Relax for all" desc="No one is held" />
            <HBtn scope={GLOBAL_HOLD_ID} current={globalMode} m="hold" label="Hold all" desc="Everyone is held" />
          </div>
        </SoftCard>

        <SoftCard hover={false}>
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm">Lock / unlock a family</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
            Search a pupil to control their family's parent-portal access — e.g. <span className="font-medium">unlock</span> a family on an approved payment plan.
          </p>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input value={lockSearch} onChange={(e) => { setLockSearch(e.target.value); setLockStudentId(''); }}
              placeholder="Search pupil by name…" className={glassFieldClass('py-2 pl-8 pr-8 text-sm')} />
            {lockSearch && <button onClick={() => { setLockSearch(''); setLockStudentId(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700"><X size={14} /></button>}
          </div>

          {q && !sel && (
            <div className="mt-2 max-h-60 overflow-y-auto rounded-xl border border-gray-100 dark:border-gray-700 divide-y divide-gray-50 dark:divide-gray-700/50">
              {matches.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 p-3">No pupils match.</p>
              ) : matches.map((s) => (
                <button key={s.id} onClick={() => setLockStudentId(s.id)} className="w-full text-left px-3 py-2 text-sm text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  {s.fullName}
                </button>
              ))}
            </div>
          )}

          {sel && (
            <div className="mt-3 rounded-xl border border-gray-200 dark:border-gray-700 p-3 space-y-3">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-semibold text-gray-800 dark:text-gray-100">{fam?.name ?? sel.fullName}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {fam ? `${fam.studentIds.length} child${fam.studentIds.length === 1 ? '' : 'ren'}` : 'No family record — this pupil only'}
                    {fam?.primaryContact?.name ? ` · ${fam.primaryContact.name}` : ''}
                    {' · '}Net balance {fmtBalance(netBalance)}
                  </div>
                </div>
                <span className={`text-xs font-semibold px-2 py-1 rounded-full ${held
                  ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                  : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'}`}>
                  {held ? 'Portal LOCKED' : 'Portal open'}{arrears ? '' : ' · not in arrears'}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <HBtn scope={holdScope} current={mode} m="auto" label="Automatic" desc="Lock only if in arrears" />
                <HBtn scope={holdScope} current={mode} m="release" label="Unlock (release)" desc="Always open — e.g. payment plan" />
                <HBtn scope={holdScope} current={mode} m="hold" label="Lock (hold)" desc="Always held until changed" />
              </div>
              <p className="text-[11px] text-gray-400">
                Effective now: <span className="font-medium">{held ? 'held' : 'open'}</span> — pupil “{mode}”, school default “{globalMode}”, {arrears ? 'in arrears' : 'no arrears'}.
              </p>
            </div>
          )}
        </SoftCard>
      </div>
    );
  }
}

/* ------------------------------------------------------------------ */
function reminderText(p: {
  contact: string; family: string; date: string; overdue: number; interest: number;
  includeInterest: boolean; lines: OverdueLine[]; schoolName: string;
}): string {
  const due = p.includeInterest ? p.overdue + p.interest : p.overdue;
  const interestLine = p.includeInterest && p.interest > 0 ? ` This includes ${fmtPula(p.interest)} in late-payment interest.` : '';
  const items = p.lines.map((l) => `  • ${l.name}: ${l.invoiceNo} — ${fmtPula(l.balance)} (${l.days} days overdue)`).join('\n');
  return [
    `Dear ${p.contact || 'Parent/Guardian'},`,
    '',
    `This is a reminder that the account for ${p.family} has an outstanding balance of ${fmtPula(due)} as at ${p.date}.${interestLine}`,
    '',
    'Outstanding invoices:',
    items,
    '',
    `Kindly settle at your earliest convenience by bank transfer (EFT) or card, quoting "${p.family}" as the payment reference. If payment has already been made, please disregard this notice.`,
    '',
    'Thank you,',
    p.schoolName,
  ].join('\n');
}

function buildReminderPdf(p: {
  contact: string; family: string; contactEmail?: string; date: string;
  overdue: number; interest: number; includeInterest: boolean; lines: OverdueLine[];
}, logo: string | null): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const MX = 16; const right = 210 - MX;
  const NAVY: [number, number, number] = [27, 58, 107];
  const INK: [number, number, number] = [31, 41, 55];
  const MUTED: [number, number, number] = [107, 114, 128];
  const RULE: [number, number, number] = [209, 213, 219];
  const setT = (c: [number, number, number]) => pdf.setTextColor(c[0], c[1], c[2]);
  const setD = (c: [number, number, number]) => pdf.setDrawColor(c[0], c[1], c[2]);
  const setF = (c: [number, number, number]) => pdf.setFillColor(c[0], c[1], c[2]);
  let y = 16;

  if (logo) { try { pdf.addImage(logo, 'PNG', MX, y - 2, 18, 18); } catch { /* ignore */ } }
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
  pdf.text(SCHOOL.name, MX + 22, y + 4);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
  pdf.text(`${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}  ·  ${SCHOOL.contact.website}`, MX + 22, y + 9);
  y += 22;
  setD(RULE); pdf.setLineWidth(0.4); pdf.line(MX, y, right, y); y += 8;

  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(15);
  pdf.text('FEE ARREARS REMINDER', MX, y);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
  pdf.text(p.date, right, y, { align: 'right' });
  y += 8;

  pdf.setFontSize(7.5); setT(MUTED); pdf.text('TO', MX, y);
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); setT(INK);
  pdf.text(p.contact || p.family, MX, y + 4.5);
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); setT(MUTED);
  pdf.text(`${p.family}${p.contactEmail ? `  ·  ${p.contactEmail}` : ''}`, MX, y + 9);
  y += 16;

  setT(INK); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9.5);
  const intro = `Our records show an outstanding balance on the account for ${p.family}. Please find the overdue invoices below and settle at your earliest convenience.`;
  const introLines = pdf.splitTextToSize(intro, right - MX) as string[];
  pdf.text(introLines, MX, y); y += introLines.length * 5 + 3;

  // Overdue table
  const cell = (txt: string, x: number, align: 'left' | 'right' = 'left') => pdf.text(txt, x, y, { align });
  setF(NAVY); pdf.rect(MX, y - 4.5, right - MX, 7, 'F');
  pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(8.5);
  cell('STUDENT / INVOICE', MX + 2); cell('Days', right - 40, 'right'); cell('Balance', right - 2, 'right');
  y += 6;
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9);
  for (const l of p.lines) {
    if (y > 250) { setT(MUTED); pdf.text('… continued', MX + 2, y); y += 6; break; }
    setT(INK); cell(`${l.name} — ${l.invoiceNo}`, MX + 2);
    setT(MUTED); cell(String(l.days), right - 40, 'right');
    setT(INK); cell(fmtPula(l.balance), right - 2, 'right');
    y += 5.6;
  }
  y += 2;

  const due = p.includeInterest ? p.overdue + p.interest : p.overdue;
  if (p.includeInterest && p.interest > 0) {
    setT(MUTED); pdf.setFontSize(8.5);
    pdf.text('Overdue', MX + 2, y); pdf.text(fmtPula(p.overdue), right - 2, y, { align: 'right' }); y += 5;
    pdf.text('Late-payment interest', MX + 2, y); pdf.text(fmtPula(p.interest), right - 2, y, { align: 'right' }); y += 5;
  }

  setF([253, 242, 242]); setD([185, 28, 28]); pdf.setLineWidth(0.5);
  pdf.rect(MX, y, right - MX, 13, 'FD');
  setT([153, 27, 27]); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(11);
  pdf.text('TOTAL AMOUNT DUE', MX + 3, y + 8.4);
  pdf.setFontSize(13);
  pdf.text(fmtPula(due), right - 3, y + 8.6, { align: 'right' });
  y += 20;

  setT(MUTED); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8);
  const foot = `Payment is by bank transfer (EFT) or card; please quote "${p.family}" as the reference. If payment has already been made, kindly disregard this notice. Thank you.`;
  pdf.text(pdf.splitTextToSize(foot, right - MX) as string[], MX, y);

  return pdf;
}

/* ------------------------------------------------------------------ */
type ArrearsRow = { family: string; contact: string; children: number; oldest: number; overdue: number; interest: number; total: number };

function buildArrearsListPdf(
  rows: ArrearsRow[],
  meta: { filterLabel: string; date: string; rate: number; totals: { overdue: number; interest: number; total: number } },
  logo: string | null,
): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const MX = 14; const right = 210 - MX;
  const NAVY: [number, number, number] = [27, 58, 107];
  const INK: [number, number, number] = [31, 41, 55];
  const MUTED: [number, number, number] = [107, 114, 128];
  const RULE: [number, number, number] = [209, 213, 219];
  const setT = (c: [number, number, number]) => pdf.setTextColor(c[0], c[1], c[2]);
  const setD = (c: [number, number, number]) => pdf.setDrawColor(c[0], c[1], c[2]);
  const setF = (c: [number, number, number]) => pdf.setFillColor(c[0], c[1], c[2]);
  let y = 16;

  // Column x-positions
  const cFam = MX + 2, cChild = right - 86, cOld = right - 66, cOver = right - 44, cInt = right - 22, cTot = right - 2;

  const header = () => {
    if (logo) { try { pdf.addImage(logo, 'PNG', MX, y - 2, 16, 16); } catch { /* ignore */ } }
    setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13);
    pdf.text(SCHOOL.name, MX + 20, y + 4);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
    pdf.text(`${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}`, MX + 20, y + 9);
    y += 20;
    setD(RULE); pdf.setLineWidth(0.4); pdf.line(MX, y, right, y); y += 7;
    setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
    pdf.text('FEE ARREARS', MX, y);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); setT(MUTED);
    pdf.text(`${meta.filterLabel}  ·  ${meta.date}`, right, y, { align: 'right' });
    y += 7;
  };

  const tableHead = () => {
    setF(NAVY); pdf.rect(MX, y - 4.5, right - MX, 7, 'F');
    pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.5);
    pdf.text('FAMILY', cFam, y);
    pdf.text('Children', cChild, y, { align: 'right' });
    pdf.text('Oldest', cOld, y, { align: 'right' });
    pdf.text('Overdue', cOver, y, { align: 'right' });
    pdf.text('Interest', cInt, y, { align: 'right' });
    pdf.text('Total', cTot, y, { align: 'right' });
    y += 6;
  };

  header();
  tableHead();
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
  for (const r of rows) {
    if (y > 275) { pdf.addPage(); y = 16; header(); tableHead(); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); }
    setT(INK);
    const famText = r.contact ? `${r.family}  (${r.contact})` : r.family;
    pdf.text((pdf.splitTextToSize(famText, cChild - cFam - 4) as string[])[0] ?? famText, cFam, y);
    setT(MUTED); pdf.text(String(r.children), cChild, y, { align: 'right' });
    pdf.text(`${r.oldest}d`, cOld, y, { align: 'right' });
    setT(INK); pdf.text(fmtPula(r.overdue), cOver, y, { align: 'right' });
    setT(MUTED); pdf.text(r.interest > 0 ? fmtPula(r.interest) : '—', cInt, y, { align: 'right' });
    setT(INK); pdf.setFont('helvetica', 'bold'); pdf.text(fmtPula(r.total), cTot, y, { align: 'right' });
    pdf.setFont('helvetica', 'normal');
    y += 5.4;
  }

  // Totals
  y += 1; setD(RULE); pdf.line(MX, y, right, y); y += 6;
  setT(INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
  pdf.text(`${rows.length} famil${rows.length === 1 ? 'y' : 'ies'}`, cFam, y);
  pdf.text(fmtPula(meta.totals.overdue), cOver, y, { align: 'right' });
  setT(MUTED); pdf.setFont('helvetica', 'normal');
  pdf.text(`@ ${meta.rate}%/mo`, cInt, y, { align: 'right' });
  setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10);
  pdf.text(fmtPula(meta.totals.total), cTot, y, { align: 'right' });

  return pdf;
}

/* ------------------------------------------------------------------ */
type StudentArrearsRow = { name: string; admissionNo: string; className: string; family: string; overdue: number; oldest: number };

function buildStudentArrearsPdf(
  rows: StudentArrearsRow[],
  meta: { filterLabel: string; date: string; total: number },
  logo: string | null,
): jsPDF {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4' });
  const MX = 14; const right = 210 - MX;
  const NAVY: [number, number, number] = [27, 58, 107];
  const INK: [number, number, number] = [31, 41, 55];
  const MUTED: [number, number, number] = [107, 114, 128];
  const RULE: [number, number, number] = [209, 213, 219];
  const setT = (c: [number, number, number]) => pdf.setTextColor(c[0], c[1], c[2]);
  const setD = (c: [number, number, number]) => pdf.setDrawColor(c[0], c[1], c[2]);
  const setF = (c: [number, number, number]) => pdf.setFillColor(c[0], c[1], c[2]);
  let y = 16;

  const cName = MX + 2, cClass = right - 92, cFam = right - 62, cOld = right - 26, cOver = right - 2;

  const header = () => {
    if (logo) { try { pdf.addImage(logo, 'PNG', MX, y - 2, 16, 16); } catch { /* ignore */ } }
    setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13);
    pdf.text(SCHOOL.name, MX + 20, y + 4);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); setT(MUTED);
    pdf.text(`${SCHOOL.contact.phonePrimary}  ·  ${SCHOOL.contact.email}`, MX + 20, y + 9);
    y += 20;
    setD(RULE); pdf.setLineWidth(0.4); pdf.line(MX, y, right, y); y += 7;
    setT(NAVY); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14);
    pdf.text('STUDENTS IN ARREARS', MX, y);
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); setT(MUTED);
    pdf.text(`${meta.filterLabel}  ·  ${meta.date}`, right, y, { align: 'right' });
    y += 7;
  };
  const tableHead = () => {
    setF(NAVY); pdf.rect(MX, y - 4.5, right - MX, 7, 'F');
    pdf.setTextColor(255, 255, 255); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(7.5);
    pdf.text('STUDENT', cName, y);
    pdf.text('Class', cClass, y);
    pdf.text('Family', cFam, y);
    pdf.text('Oldest', cOld, y, { align: 'right' });
    pdf.text('Overdue', cOver, y, { align: 'right' });
    y += 6;
  };

  header();
  tableHead();
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5);
  for (const r of rows) {
    if (y > 275) { pdf.addPage(); y = 16; header(); tableHead(); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8.5); }
    setT(INK);
    const nm = r.admissionNo ? `${r.name}  (${r.admissionNo})` : r.name;
    pdf.text((pdf.splitTextToSize(nm, cClass - cName - 3) as string[])[0] ?? nm, cName, y);
    setT(MUTED);
    pdf.text((pdf.splitTextToSize(r.className, cFam - cClass - 3) as string[])[0] ?? r.className, cClass, y);
    pdf.text((pdf.splitTextToSize(r.family, cOld - cFam - 14) as string[])[0] ?? r.family, cFam, y);
    pdf.text(`${r.oldest}d`, cOld, y, { align: 'right' });
    setT(INK); pdf.setFont('helvetica', 'bold'); pdf.text(fmtPula(r.overdue), cOver, y, { align: 'right' });
    pdf.setFont('helvetica', 'normal');
    y += 5.4;
  }

  y += 1; setD(RULE); pdf.line(MX, y, right, y); y += 6;
  setT(INK); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9);
  pdf.text(`${rows.length} student${rows.length === 1 ? '' : 's'} in arrears`, cName, y);
  setT(NAVY); pdf.setFontSize(10);
  pdf.text(fmtPula(meta.total), cOver, y, { align: 'right' });

  return pdf;
}
